import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client'

interface DisclaimerCurrent {
  id: string
  version: number
  title: string
  content: string
  published_at: string | null
}

interface DisclaimerHistoryRow {
  version: number
  title: string
  is_published: boolean
  published_at: string | null
  updated_by: string | null
  requires_reacceptance?: boolean
  acceptances: number
  suppressions: number
  last_accepted_at: string | null
}

interface AdminDisclaimerResponse {
  current: DisclaimerCurrent | null
  history: DisclaimerHistoryRow[]
}

export default function Disclaimer() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery<AdminDisclaimerResponse>({
    queryKey: ['admin-disclaimer'],
    queryFn: () => apiRequest<AdminDisclaimerResponse>('/api/admin/disclaimer'),
  })

  const [title, setTitle] = useState('Disclaimer and Disclosure')
  const [content, setContent] = useState('')
  // Checked = new terms, everyone reads it again (the default and the safe
  // choice). Unchecked = an edit that does not change what readers agree to;
  // earlier acceptance and "don't show again" carry over. Reset to checked
  // after every publish so the opt-out is a deliberate act each time.
  const [requiresReacceptance, setRequiresReacceptance] = useState(true)
  const [seeded, setSeeded] = useState(false)

  // Seed the editor from the current version once (don't clobber edits on refetch).
  useEffect(() => {
    if (!seeded && data?.current) {
      setTitle(data.current.title || 'Disclaimer and Disclosure')
      setContent(data.current.content || '')
      setSeeded(true)
    } else if (!seeded && data && !data.current) {
      setSeeded(true) // nothing published yet — start with an empty editor
    }
  }, [data, seeded])

  const publish = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/disclaimer', {
        method: 'POST',
        body: JSON.stringify({ content, title, requires_reacceptance: requiresReacceptance }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disclaimer'] })
      setRequiresReacceptance(true)
    },
  })

  const onPublish = () => {
    if (!content.trim()) return
    const ok = window.confirm(
      requiresReacceptance
        ? 'Publish new Freemium Terms of Service?\n\n' +
            'Every free-trial user will see it again and must accept, including anyone who ' +
            'ticked "Don\u2019t show again". The acceptance count starts again from zero.'
        : 'Publish as an edit that does NOT require re-acceptance?\n\n' +
            'Free-trial users who accepted an earlier version will not be prompted again, and ' +
            'their "Don\u2019t show again" choice carries over. Only readers who never accepted ' +
            'any version will see it.\n\n' +
            'Use this for wording fixes only. If the terms themselves changed, cancel and ' +
            'leave the box ticked.',
    )
    if (ok) publish.mutate()
  }

  if (isLoading) return <div className="p-6">Loading…</div>
  if (error) return <div className="p-6 text-error">Failed to load the disclaimer.</div>

  const current = data?.current
  const history = data?.history ?? []
  // The live version's row. Acceptance is per document, so a published version's
  // count is the only one that describes what people have agreed to right now.
  const liveRow = current ? history.find((h) => h.version === current.version) : undefined

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="page-title">Freemium Terms of Service</h1>
        <p className="text-base-content/60 text-sm mt-1">
          The blocking popup shown to FREE-TRIAL users only &mdash; Accept button and a
          &ldquo;Don&rsquo;t show again&rdquo; checkbox. Staff and subscribers never see it.
          Accepting does not silence it: it returns once per sign-in until the reader ticks
          that box. Publishing new text clears both acceptance and suppression, so changed
          terms reach everybody &mdash; including readers who opted out of the old wording.
        </p>
      </div>

      <div className="stats stats-vertical sm:stats-horizontal bg-base-100 border border-base-300 w-full">
        <div className="stat">
          <div className="stat-title">Accepted</div>
          <div className="stat-value tabular-nums">{liveRow?.acceptances ?? 0}</div>
          <div className="stat-desc">
            {current ? `people have accepted v${current.version}` : 'nothing published yet'}
          </div>
        </div>
        <div className="stat">
          <div className="stat-title">Chose &ldquo;Don&rsquo;t show again&rdquo;</div>
          <div className="stat-value tabular-nums">{liveRow?.suppressions ?? 0}</div>
          <div className="stat-desc">
            {liveRow?.acceptances
              ? `${Math.round((100 * (liveRow.suppressions ?? 0)) / liveRow.acceptances)}% of those who accepted`
              : 'no acceptances yet'}
          </div>
        </div>
        <div className="stat">
          <div className="stat-title">Last accepted</div>
          <div className="stat-value text-lg font-normal">
            {liveRow?.last_accepted_at
              ? new Date(liveRow.last_accepted_at).toLocaleString()
              : '—'}
          </div>
          <div className="stat-desc">counts reset when you publish new text</div>
        </div>
      </div>

      <div className="alert alert-warning text-sm">
        <span>
          {current
            ? `Current live version: v${current.version}${
                current.published_at
                  ? ' · published ' + new Date(current.published_at).toLocaleString()
                  : ''
              }`
            : 'Nothing published yet — free-trial users see no disclaimer until you publish one.'}
        </span>
      </div>

      <div className="card bg-base-100 border border-base-300">
        <div className="card-body gap-4">
          <div>
            <label className="label"><span className="label-text font-medium">Title</span></label>
            <input
              className="input input-bordered w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Disclaimer and Disclosure"
            />
          </div>
          <div>
            <label className="label">
              <span className="label-text font-medium">Content</span>
              <span className="label-text-alt text-base-content/50">Shown to free-trial users in the popup. Markdown links work: [text](https://…)</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full font-mono text-sm min-h-[360px]"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter the disclaimer text…"
            />
          </div>

          {/* Not a DaisyUI `label`: that class sets white-space: nowrap, which is
              right for a one-word label and wrong for a sentence of guidance. */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-primary mt-0.5 shrink-0"
              checked={requiresReacceptance}
              onChange={(e) => setRequiresReacceptance(e.target.checked)}
            />
            <span className="min-w-0 text-sm">
              <span className="font-medium">Require everyone to accept again</span>
              <span className="block text-base-content/60 text-xs mt-0.5">
                Leave ticked when the terms change. Untick only for a wording fix — readers who
                accepted an earlier version keep their acceptance and their &ldquo;Don&rsquo;t show
                again&rdquo; choice, and are not prompted.
              </span>
            </span>
          </label>

          {publish.isError && (
            <p className="text-error text-sm">
              {(publish.error as Error)?.message || 'Publish failed.'}
            </p>
          )}
          {publish.isSuccess && (
            <p className="text-success text-sm">
              Published — free-trial users will see it again on next load.
            </p>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              className="btn btn-primary"
              onClick={onPublish}
              disabled={publish.isPending || !content.trim()}
            >
              {publish.isPending ? 'Publishing…' : 'Publish new version'}
            </button>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body">
            <h2 className="font-semibold mb-2">Version history</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Published</th>
                    <th>By</th>
                    <th className="text-right">Accepted</th>
                    <th className="text-right">Don&rsquo;t show again</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.version}>
                      <td>
                        v{h.version}
                        {current && h.version === current.version && (
                          <span className="badge badge-success badge-sm ml-2">live</span>
                        )}
                        {h.requires_reacceptance === false && (
                          <span
                            className="badge badge-ghost badge-sm ml-2"
                            title="Published as a wording fix: earlier acceptances carried over, nobody was re-prompted. Its own acceptance count only reflects readers who had never accepted any version."
                          >
                            edit
                          </span>
                        )}
                      </td>
                      <td>{h.published_at ? new Date(h.published_at).toLocaleString() : '—'}</td>
                      <td className="text-base-content/70">{h.updated_by || '—'}</td>
                      <td className="text-right tabular-nums">{h.acceptances}</td>
                      <td className="text-right tabular-nums text-base-content/70">{h.suppressions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
